import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  ShieldCheck,
  Tag,
  Trash2,
  Save,
  Download,
  Calendar,
  Building2,
  Receipt as ReceiptIcon,
  AlertTriangle,
  Zap,
  X,
  Check,
  QrCode,
  Copy,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { ComplianceStatuteModal } from "@/components/ComplianceStatuteModal";

const CAT_LABELS = {
  meals: "Meals & Entertainment",
  office_supplies: "Office Supplies",
  electronics: "Electronics",
  transportation: "Transportation",
  groceries: "Groceries",
  software: "Software",
  travel: "Travel",
  entertainment: "Entertainment",
  other: "Other",
};

// ---------- Mock QR Splitter Modal (deterministic pattern from receipt id) ----------
function _qrHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return h >>> 0;
}
function _qrMatrix(seed, size = 12) {
  const grid = [];
  let s = seed || 1;
  for (let y = 0; y < size; y++) {
    const row = [];
    for (let x = 0; x < size; x++) {
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      row.push((s >> 8) & 1);
    }
    grid.push(row);
  }
  // Corner "position markers" like a real QR
  const mark = (ox, oy) => {
    for (let y = 0; y < 3; y++) for (let x = 0; x < 3; x++) grid[oy + y][ox + x] = 1;
    for (let y = 1; y < 2; y++) for (let x = 1; x < 2; x++) grid[oy + y][ox + x] = 0;
  };
  mark(0, 0); mark(size - 3, 0); mark(0, size - 3);
  return grid;
}

function QRSplitModal({ receipt, onClose }) {
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const shareUrl = `${window.location.origin}/split/${receipt?.id || "receipt"}?amt=${Number(receipt?.total || 0).toFixed(2)}`;
  const seed = _qrHash(receipt?.id || "seed");
  const matrix = _qrMatrix(seed, 12);

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  const triggerShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Split receipt: ${receipt?.merchant}`,
          text: `Splitting a $${Number(receipt?.total || 0).toFixed(2)} receipt at ${receipt?.merchant}. Tap to pay your share.`,
          url: shareUrl,
        });
        setShared(true);
        toast.success("Share sheet opened");
      } catch {
        copyLink();
        toast.info("Link copied — share it anywhere");
      }
    } else {
      copyLink();
      toast.info("Link copied — share it anywhere");
    }
    setShared(true);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      data-testid="qr-split-modal"
      style={{ background: "radial-gradient(circle at 50% 30%, rgba(16,185,129,0.12), rgba(2,6,23,0.92) 60%)", backdropFilter: "blur(16px)" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 15, scale: 0.97 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg, rgba(15,23,42,0.92) 0%, rgba(2,6,23,0.95) 100%)",
          border: "1px solid rgba(16,185,129,0.3)",
          boxShadow: "0 25px 60px -15px rgba(16,185,129,0.3)",
        }}
      >
        <button
          onClick={onClose}
          data-testid="qr-close-btn"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors z-10"
          aria-label="Close"
        >
          <X size={15} />
        </button>
        <div className="px-8 pt-8 pb-6">
          <div className="text-emerald-400 font-mono text-[10px] uppercase tracking-[0.35em] flex items-center gap-2">
            <QrCode size={11} /> Split via QR
          </div>
          <div className="mt-2 text-2xl font-bold text-white tracking-tight">
            {receipt?.merchant} · ${Number(receipt?.total || 0).toFixed(2)}
          </div>
          <div className="mt-1 text-slate-400 text-sm">Have others scan to pay their share.</div>

          <div className="mt-6 mx-auto w-fit p-5 rounded-2xl bg-white shadow-2xl" data-testid="qr-code-block">
            <svg width="192" height="192" viewBox="0 0 12 12" shapeRendering="crispEdges" xmlns="http://www.w3.org/2000/svg">
              <rect width="12" height="12" fill="#ffffff" />
              {matrix.flatMap((row, y) =>
                row.map((cell, x) =>
                  cell ? <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="#020617" /> : null
                )
              )}
            </svg>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2">
            <button
              onClick={copyLink}
              data-testid="qr-copy-link-btn"
              className="h-11 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy link"}
            </button>
            <button
              onClick={triggerShare}
              data-testid="qr-share-btn"
              className="btn-neon h-11 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center justify-center gap-2"
            >
              <Share2 size={14} /> {shared ? "Shared" : "Share"}
            </button>
          </div>
          <div className="mt-4 text-center text-[9px] font-mono text-slate-600 uppercase tracking-[0.25em]">
            Mock QR · Preview / placeholder for split payments
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function CapitalAdvanceModal({ receipt, onClose }) {
  const total = Number(receipt?.total || 0);
  const advance = +(total * 0.9).toFixed(2);
  const fee = +(total * 0.03).toFixed(2);
  const net = +(advance - fee).toFixed(2);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState("processing"); // processing | complete
  const [signalSent, setSignalSent] = useState(false);
  const DURATION_MS = 60000;

  useEffect(() => {
    const start = performance.now();
    let rafId;
    const tick = (now) => {
      const p = Math.min(100, ((now - start) / DURATION_MS) * 100);
      setProgress(p);
      if (p < 100) {
        rafId = requestAnimationFrame(tick);
      } else {
        setStage("complete");
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  useEffect(() => {
    if (stage === "complete" && !signalSent && receipt?.id) {
      setSignalSent(true);
      api.post("/capital-advance/waitlist", {
        receipt_id: receipt.id,
        receipt_total: total,
        advance_amount: advance,
      }).catch(() => {});
    }
  }, [stage, signalSent, receipt, total, advance]);

  const stages = [
    { at: 0, label: "Signal received" },
    { at: 25, label: "Underwriting" },
    { at: 55, label: "Risk model" },
    { at: 80, label: "Treasury routing" },
    { at: 100, label: "Funds deposited" },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      data-testid="capital-advance-modal"
      style={{ background: "radial-gradient(circle at 50% 20%, rgba(16,185,129,0.15), rgba(2,6,23,0.92) 60%)", backdropFilter: "blur(16px)" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.96 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-lg rounded-3xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg, rgba(15,23,42,0.9) 0%, rgba(2,6,23,0.95) 100%)",
          border: "1px solid rgba(16,185,129,0.35)",
          boxShadow: "0 30px 80px -20px rgba(16,185,129,0.35), inset 0 1px 0 rgba(255,255,255,0.05)",
          transform: "translate3d(0,0,0)",
        }}
      >
        <button
          onClick={onClose}
          data-testid="advance-close-btn"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors z-10"
          aria-label="Close"
        >
          <X size={15} />
        </button>

        <div className="px-8 pt-8 pb-4 relative">
          <div className="text-emerald-400 font-mono text-[10px] uppercase tracking-[0.35em] flex items-center gap-2">
            <Zap size={11} /> Capital Advance · Business Tier
          </div>
          <div className="mt-3 text-3xl font-bold text-white tracking-tighter">
            {stage === "complete" ? "Deposited." : "Provisioning capital…"}
          </div>
          <div className="mt-1 text-slate-400 text-sm">
            {stage === "complete"
              ? `$${net.toFixed(2)} routed to your default account.`
              : "Simulated processing · no funds are actually moved."}
          </div>

          {/* 3D split visualization */}
          <div className="mt-8 relative h-32" style={{ perspective: "1200px" }} data-testid="advance-split-viz">
            <motion.div
              initial={{ rotateX: 25, rotateY: -12, opacity: 0 }}
              animate={{ rotateX: 12, rotateY: -6, opacity: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 flex items-stretch gap-3"
              style={{ transformStyle: "preserve-3d" }}
            >
              <div
                className="rounded-2xl flex flex-col items-center justify-center border relative"
                style={{
                  flex: 9,
                  background: "linear-gradient(180deg, rgba(16,185,129,0.35) 0%, rgba(16,185,129,0.08) 100%)",
                  borderColor: "rgba(16,185,129,0.5)",
                  transform: "translate3d(0,0,20px)",
                  boxShadow: "0 20px 40px -20px rgba(16,185,129,0.5)",
                }}
              >
                <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-emerald-200/80">Advance · 90%</div>
                <div className="mt-1 text-2xl font-bold font-mono text-white" data-testid="advance-amount">
                  ${advance.toFixed(2)}
                </div>
              </div>
              <div
                className="rounded-2xl flex flex-col items-center justify-center border"
                style={{
                  flex: 1,
                  background: "rgba(245,158,11,0.15)",
                  borderColor: "rgba(245,158,11,0.35)",
                  transform: "translate3d(0,0,10px)",
                }}
              >
                <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-amber-300/80">Fee</div>
                <div className="mt-1 text-sm font-bold font-mono text-white">${fee.toFixed(2)}</div>
              </div>
            </motion.div>
          </div>

          <div className="mt-6 flex items-baseline justify-between">
            <div className="text-slate-500 text-xs uppercase tracking-widest font-mono">Net to you</div>
            <div className="font-mono text-xl font-bold text-emerald-300" data-testid="advance-net">
              ${net.toFixed(2)}
            </div>
          </div>

          {/* Progress bar w/ translate3d */}
          <div className="mt-6 relative h-2 rounded-full overflow-hidden bg-white/5 border border-white/10" data-testid="advance-progress-track">
            <div
              className="h-full rounded-full"
              style={{
                width: "100%",
                background: "linear-gradient(90deg, #10B981 0%, #34d399 50%, #10B981 100%)",
                transform: `translate3d(${progress - 100}%, 0, 0)`,
                transition: "transform 120ms linear",
                boxShadow: "0 0 20px rgba(16,185,129,0.5)",
              }}
              data-testid="advance-progress-fill"
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest">
            <span className="text-slate-500">
              {stage === "complete" ? "Complete" : `Processing ${Math.floor(progress)}%`}
            </span>
            <span className="text-emerald-400">
              ETA {Math.max(0, Math.ceil((100 - progress) * 0.6))}s
            </span>
          </div>

          {/* Stage rail */}
          <div className="mt-6 grid grid-cols-5 gap-1" data-testid="advance-stages">
            {stages.map((s, i) => {
              const active = progress >= s.at;
              return (
                <div key={s.label} className="flex flex-col items-center gap-1.5">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center border transition-all duration-500 ${
                      active
                        ? "bg-emerald-500 border-emerald-300 text-slate-950"
                        : "bg-white/5 border-white/10 text-slate-600"
                    }`}
                    style={{ transform: active ? "translate3d(0,-2px,0) scale(1.05)" : "translate3d(0,0,0)" }}
                  >
                    {active ? <Check size={11} strokeWidth={3} /> : <span className="text-[9px] font-mono">{i + 1}</span>}
                  </div>
                  <div
                    className={`text-[8px] font-mono uppercase tracking-widest text-center leading-tight ${
                      active ? "text-emerald-300" : "text-slate-600"
                    }`}
                  >
                    {s.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-8 pb-8 pt-2">
          <button
            onClick={onClose}
            disabled={stage !== "complete"}
            data-testid="advance-confirm-btn"
            className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:bg-white/5 disabled:text-slate-500 disabled:cursor-not-allowed text-slate-950 font-semibold text-sm tracking-tight transition-all"
          >
            {stage === "complete" ? "Done" : "Please wait…"}
          </button>
          <div className="mt-3 text-center text-[9px] font-mono text-slate-600 uppercase tracking-[0.25em]">
            Simulated · Placeholder for future capital-advance product
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function ReceiptDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [newTag, setNewTag] = useState("");
  const [boom, setBoom] = useState(0);
  const [disc, setDisc] = useState(null);
  const [showAdvance, setShowAdvance] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [statuteOpen, setStatuteOpen] = useState(false);
  const navigate = useNavigate();
  const blobRef = useRef(null);

  useEffect(() => {
    api.get(`/receipts/${id}`).then((res) => setR(res.data));
    api
      .get(`/receipts/${id}/discrepancy`)
      .then((res) => setDisc(res.data))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!r?.image_path) return;
    // Fetch image as blob with credentials
    let cancelled = false;
    fetch(`${API}/files/${r.image_path}`, { credentials: "include" })
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (blob && !cancelled) {
          const url = URL.createObjectURL(blob);
          blobRef.current = url;
          setImgUrl(url);
        }
      });
    return () => {
      cancelled = true;
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    };
  }, [r?.image_path]);

  const save = async (patch) => {
    const { data } = await api.patch(`/receipts/${id}`, patch);
    setR(data);
    toast.success("Saved");
  };

  const toggleDeductible = async () => {
    const next = !r.tax_deductible;
    if (next) setBoom((b) => b + 1);
    const { data } = await api.patch(`/receipts/${id}`, { tax_deductible: next });
    setR(data);
    toast.success(next ? "Flagged as tax deductible" : "Removed deductible flag");
  };

  const deleteReceipt = async () => {
    if (!confirm("Delete this receipt?")) return;
    await api.delete(`/receipts/${id}`);
    toast.success("Deleted");
    navigate("/receipts");
  };

  const addTag = async () => {
    if (!newTag.trim()) return;
    const tags = Array.from(new Set([...(r.tags || []), newTag.trim().toLowerCase()]));
    await save({ tags });
    setNewTag("");
  };
  const removeTag = async (t) => {
    const tags = (r.tags || []).filter((x) => x !== t);
    await save({ tags });
  };

  if (!r) {
    return (
      <DashboardLayout>
        <div className="text-slate-500 font-mono text-sm tracking-widest text-center py-12">
          LOADING…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center justify-between fade-up">
        <button
          onClick={() => navigate("/receipts")}
          className="text-slate-400 hover:text-white text-sm flex items-center gap-2"
          data-testid="back-to-receipts"
        >
          <ArrowLeft size={16} /> Back to receipts
        </button>
        <button
          onClick={deleteReceipt}
          data-testid="delete-receipt-btn"
          className="text-red-400 hover:text-red-300 text-sm flex items-center gap-2 h-10 px-3 rounded-lg hover:bg-red-500/10 border border-red-500/20"
        >
          <Trash2 size={14} /> Delete
        </button>
      </div>

      <div className="mb-4 flex justify-end">
        <button
          onClick={() => setShowQR(true)}
          data-testid="qr-split-btn"
          className="text-emerald-300 hover:text-emerald-200 text-sm flex items-center gap-2 h-9 px-3 rounded-lg bg-emerald-500/10 border border-emerald-500/25 hover:bg-emerald-500/20 transition-colors"
        >
          <QrCode size={13} /> Split via QR
        </button>
      </div>

      <div className="grid lg:grid-cols-5 gap-6 fade-up">
        {/* Image */}
        <div className="lg:col-span-2 glass rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
              IRS-COMPLIANT ARCHIVE
            </span>
            <span className="text-[10px] font-mono text-emerald-400 uppercase tracking-widest">
              Certified
            </span>
          </div>
          <div className="p-5 flex items-center justify-center min-h-[400px] bg-slate-950/60">
            {imgUrl ? (
              <img
                src={imgUrl}
                alt={r.merchant}
                className="max-w-full max-h-[560px] rounded-lg shadow-lg"
                data-testid="receipt-image"
              />
            ) : (
              <div className="text-center py-16 px-6">
                <ReceiptIcon size={40} className="text-slate-600 mx-auto mb-4" />
                <div className="text-slate-500 text-sm">
                  No image uploaded (this is a demo receipt).
                </div>
                <div className="text-slate-600 text-xs font-mono mt-2 uppercase tracking-widest">
                  Original archive preserved
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-3 space-y-6">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-slate-400 text-xs uppercase tracking-[0.22em] mb-1 flex items-center gap-1.5">
                  <Building2 size={12} /> Merchant
                </div>
                <div className="text-2xl font-bold text-white tracking-tight" data-testid="detail-merchant">
                  {r.merchant}
                </div>
                <div className="mt-2 text-sm text-slate-400 flex items-center gap-1.5 font-mono">
                  <Calendar size={12} /> {r.date}
                </div>
              </div>
              <button
                onClick={toggleDeductible}
                data-testid="detail-deductible-badge"
                className={`shockwave-host relative inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold px-3 py-1.5 rounded border transition-colors ${
                  r.tax_deductible
                    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/25 hover:bg-emerald-500/25 motion-slide"
                    : "bg-white/5 text-slate-400 border-white/10 hover:text-emerald-300 hover:border-emerald-500/30"
                }`}
                key={`ded-${boom}`}
              >
                <ShieldCheck size={12} />
                {r.tax_deductible ? "Tax Deductible" : "Flag as Deductible"}
                {boom > 0 && r.tax_deductible && (
                  <>
                    <span className="ring" key={`r1-${boom}`} />
                    <span className="ring delay" key={`r2-${boom}`} />
                    <span className="flash" key={`f-${boom}`} />
                  </>
                )}
              </button>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4">
              <div>
                <div className="text-slate-500 text-xs uppercase tracking-widest">Subtotal</div>
                <div className="font-mono text-lg font-semibold text-slate-200 mt-1">
                  ${Number(r.subtotal || 0).toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-slate-500 text-xs uppercase tracking-widest">Sales Tax</div>
                <div className="font-mono text-lg font-semibold text-slate-200 mt-1">
                  ${Number(r.tax || 0).toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-slate-500 text-xs uppercase tracking-widest">Total</div>
                <div
                  className="font-mono text-2xl font-bold text-emerald-400 mt-1"
                  data-testid="detail-total"
                >
                  {r.currency && r.currency !== "USD" ? `${r.currency} ` : "$"}
                  {Number(r.total || 0).toFixed(2)}
                </div>
                {r.total_usd && r.currency !== "USD" ? (
                  <div className="mt-1 text-[10px] uppercase tracking-widest font-mono text-slate-400" data-testid="fx-usd">
                    ≈ ${Number(r.total_usd).toFixed(2)} USD
                    {r.fx_rate ? (
                      <span className="text-slate-500"> · rate {Number(r.fx_rate).toFixed(4)}</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest font-medium px-2 py-1 rounded bg-white/5 text-slate-300 border border-white/5">
                {CAT_LABELS[r.category] || r.category}
              </span>
              {r.compliance_verification_token && (
                <button
                  type="button"
                  onClick={() => setStatuteOpen(true)}
                  data-testid="compliance-token-inline"
                  data-variant={r.compliance_verification_token.badge}
                  title={`${r.compliance_verification_token.title} — click for the full IRS statute`}
                  className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded border inline-flex items-center gap-1 cursor-pointer hover:brightness-125 transition ${
                    r.compliance_verification_token.badge === "audit-safe"
                      ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/40 audit-safe-glow"
                      : r.compliance_verification_token.badge === "partial-deductible"
                      ? "bg-amber-500/15 text-amber-200 border-amber-500/35"
                      : "bg-slate-500/10 text-slate-400 border-slate-500/25"
                  }`}
                >
                  {r.compliance_verification_token.code} · {r.compliance_verification_token.deductible_pct}%
                </button>
              )}
              {(r.tags || []).map((t) => (
                <button
                  key={t}
                  onClick={() => removeTag(t)}
                  data-testid={`tag-${t}`}
                  className="text-[10px] uppercase tracking-widest font-medium px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 hover:bg-red-500/15 hover:text-red-300 hover:border-red-500/30 transition-colors inline-flex items-center gap-1"
                >
                  <Tag size={9} /> {t} ×
                </button>
              ))}
              <div className="flex items-center gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="add tag"
                  className="h-7 text-xs bg-white/5 border-white/10 w-28"
                  onKeyDown={(e) => e.key === "Enter" && addTag()}
                  data-testid="tag-input"
                />
                <button
                  onClick={addTag}
                  className="text-xs text-emerald-300 hover:text-emerald-200"
                >
                  + ADD
                </button>
              </div>
            </div>

            {(user?.subscription_tier === "business" && (user?.subscription_status === "active" || user?.subscription_status === "pro_tier" || user?.subscription_status === "active_business")) && Number(r.total || 0) > 500 && (
              <div className="mt-6 pt-6 border-t border-white/5">
                <button
                  onClick={() => setShowAdvance(true)}
                  data-testid="capital-advance-btn"
                  className="capital-advance-cta w-full h-14 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm uppercase tracking-[0.2em] flex items-center justify-center gap-3 relative overflow-hidden"
                >
                  <Zap size={16} className="relative z-10" />
                  <span className="relative z-10">Advance Cash Against This Receipt</span>
                  <span className="advance-glow" />
                </button>
                <div className="mt-2 text-center text-[10px] font-mono text-slate-500 uppercase tracking-[0.2em]">
                  Business Tier · Instant liquidity up to 90% of receipt value
                </div>
              </div>
            )}
          </div>

          {/* Line Items */}
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-white tracking-tight">Line Items</h3>
              <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">
                {r.line_items?.length || 0} items
              </span>
            </div>
            {(!r.line_items || r.line_items.length === 0) ? (
              <div className="text-sm text-slate-500 py-6 text-center">
                No line items extracted.
              </div>
            ) : (
              <div className="space-y-2">
                {r.line_items.map((li, i) => (
                  <div
                    key={i}
                    data-testid={`receipt-line-item-${i}`}
                    className="chroma-strip rounded-xl px-4 py-3 flex items-center justify-between gap-4"
                  >
                    <div>
                      <div className="text-white text-sm">{li.description}</div>
                      <div className="text-xs text-slate-500 font-mono mt-0.5">
                        Qty {Number(li.quantity).toString()}
                      </div>
                    </div>
                    <div className="font-mono font-semibold text-emerald-300">
                      ${Number(li.price).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showAdvance && (
          <CapitalAdvanceModal receipt={r} onClose={() => setShowAdvance(false)} />
        )}
        {showQR && (
          <QRSplitModal receipt={r} onClose={() => setShowQR(false)} />
        )}
      </AnimatePresence>
      <ComplianceStatuteModal
        open={statuteOpen}
        token={r?.compliance_verification_token}
        onClose={() => setStatuteOpen(false)}
      />
    </DashboardLayout>
  );
}
